/* eslint-disable no-nested-ternary */
import {Typography} from '@mui/material';
import {
  ComponentBox,
  EmptyResult,
  formattedPropertyModel,
  DlMuiUserIconMultiColor,
  TranslateUtils,
  DlMuiHospitalizationDepartmentIcon,
  LoadingSpinner,
} from '@wallaby-ui/core';
import classNames from 'classnames';
import React, {useMemo} from 'react';
import {defineMessages, useIntl} from 'react-intl';
import {HaidiSchema} from 'src/js/common/types';

import styles from './PatientInfo.less';

export const I18N = defineMessages({
  TITLE: {
    id: 'patient.detail.info.title',
    defaultMessage: 'Patient data',
    description: 'title text for patient info box',
  },
  HELP: {
    id: 'patient.detail.info.help',
    defaultMessage: 'This box is showing the patient data.',
    description: 'help text for patient info box',
  },
});

interface OwnProps {
  isLoading: boolean;
  data: HaidiSchema.components['schemas']['PatientSimpleDto'] | undefined;
}

type Props = OwnProps;

function getInitials(name: string): string {
  if (name) {
    const second = name.split(/\s/)[1];
    if (second) {
      return `${name[0]}${second[0]}`;
    }
    return name[0];
  }
  return '';
}

export const PatientInfo: React.FC<Props> = (props) => {
  const {isLoading, data} = props;
  const intl = useIntl();
  const propDefinition = useMemo(() => {
    return new formattedPropertyModel.FormattedProperty({
      type: formattedPropertyModel.FORMATTED_PROPERTY_TYPE_DATE,
    });
  }, []);

  const formattedDateValue = formattedPropertyModel.FormattedProperty.formatValue(propDefinition, data?.birthDate, intl);

  const formattedGender = data?.genderCode
    ? `${TranslateUtils.translate(`resources.gender_labels.${data?.genderCode}`, intl)} (${data?.genderCode})`
    : '';
  return (
    <div className={classNames(styles.dlPatientInfoWrapper)} style={{borderRadius: '15px'}}>
      <ComponentBox hasScrollbar style={{margin: '0px', overflow: 'hidden'}}>
        {isLoading ? (
          <LoadingSpinner isVisible text='' />
        ) : !data ? (
          <EmptyResult />
        ) : (
          <>
            <div
              style={{
                height: '128px',
                background: '#E2E8F2',
                marginRight: '-60px',
                marginLeft: '-60px',
                marginTop: '-12px',
              }}
            />

            <div
              style={{
                height: '128px',
                width: '128px',
                background: '#D8E8FF',
                marginTop: '-64px',
                borderRadius: '50%',
                alignSelf: 'center',
                marginBottom: '24px',
                display: 'flex',
              }}
            >
              <Typography fontSize='42px' fontWeight='600' style={{color: '#154FA6', flexGrow: 1}} align='center' alignSelf='center'>
                {getInitials(data?.fullName)}
              </Typography>
            </div>
            <div className={styles.dlPatientInfoLabelValuesWrapper} key={data.srcPatientId}>
              <Typography fontSize='24px' fontWeight='600' align='center' style={{color: '#10264C'}}>
                {data.fullName}
              </Typography>
              <Typography fontSize='14px' fontWeight='400' align='center' style={{marginTop: '10px', color: '#9BA8BD'}}>
                {data.srcPatientId}
              </Typography>
              <Typography fontSize='14px' fontWeight='400' align='center' style={{color: '#9BA8BD'}}>
                {data.identificationNumber}
              </Typography>

              <div
                style={{
                  margin: '10px',
                  borderRadius: '15px',
                  width: '100%',
                  background: '#F4F6FA',
                  padding: '20px',
                }}
              >
                <div style={{display: 'flex', flexDirection: 'row'}}>
                  <DlMuiUserIconMultiColor style={{alignSelf: 'center'}} />
                  <div style={{flexGrow: 1, marginRight: '54px'}}>
                    {formattedGender || formattedDateValue ? (
                      <>
                        <Typography fontSize='14px' fontWeight='600' align='center' style={{color: '#10264C'}}>
                          {formattedGender}
                        </Typography>
                        <Typography fontSize='14px' fontWeight='600' align='center' style={{color: '#10264C'}}>
                          {formattedDateValue}
                        </Typography>
                      </>
                    ) : (
                      <Typography fontSize='14px' fontWeight='600' align='center' style={{color: '#10264C'}}>
                        {TranslateUtils.translate('patient.list.informationNotAvailable', intl)}
                      </Typography>
                    )}
                  </div>
                </div>
              </div>

              {data.lastHospitalizationOrganizationName && (
                <div
                  style={{
                    margin: '10px',
                    borderRadius: '15px',
                    width: '100%',
                    background: '#F4F6FA',
                    padding: '20px',
                  }}
                >
                  <div style={{display: 'flex', flexDirection: 'row'}}>
                    <DlMuiHospitalizationDepartmentIcon style={{alignSelf: 'center'}} />
                    <div style={{flexGrow: 1, marginRight: '54px'}}>
                      <Typography fontSize='14px' fontWeight='600' align='center' style={{color: '#10264C'}}>
                        {TranslateUtils.translate('patient.list.grid.lastHospitalizationOrganization', intl)}
                      </Typography>
                      <Typography fontSize='14px' fontWeight='400' align='center' style={{color: '#10264C'}}>
                        {data.lastHospitalizationOrganizationName}
                      </Typography>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </ComponentBox>
    </div>
  );
};

PatientInfo.displayName = 'PatientInfo';
